import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, ChevronRight, Flame, Award } from 'lucide-react';

/**
 * Post-Session Score Screen overlay shown after the final drill of the day is completed.
 * 7 sections: Big Score, Rank, Breakdown, Streak, Social Pressure, CTA, Micro Copy.
 */
export default function PostSessionScore({ open, scoreSummary, dayLabel, onContinue }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-[#060913]/95 flex items-center justify-center px-4 overflow-y-auto py-8"
          data-testid="day-complete-overlay"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
            className="bg-[#0B132B] border border-[#1C2B54] rounded-2xl p-5 max-w-sm w-full shadow-2xl space-y-4"
          >
            {scoreSummary ? (
              <>
                {/* 1. TOP — BIG SCORE */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300, delay: 0.3 }}
                  className="text-center pt-2"
                >
                  <p className="text-[10px] text-[#64748B] uppercase tracking-widest mb-1">
                    {dayLabel} Complete
                  </p>
                  <p
                    className="font-['Barlow_Condensed'] text-6xl font-black text-white"
                    data-testid="today-score"
                  >
                    {scoreSummary.today_score}
                  </p>
                  {scoreSummary.score_diff !== 0 && (
                    <p
                      className={`text-sm font-bold mt-1 ${scoreSummary.score_diff > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}
                      data-testid="score-diff"
                    >
                      {scoreSummary.score_diff > 0 ? '+' : ''}
                      {scoreSummary.score_diff} from yesterday
                    </p>
                  )}
                </motion.div>

                {/* 2. RANK + COMPETITION */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-[#060913] rounded-xl p-3 text-center"
                  data-testid="rank-section"
                >
                  <p className="text-white text-sm font-semibold">
                    You're <span className="text-[#2563EB] font-black">#{scoreSummary.rank}</span> among all players
                    {scoreSummary.has_friends && (
                      <>
                        , <span className="text-[#00E5FF] font-black">#{scoreSummary.friend_rank}</span> among friends
                      </>
                    )}
                    !
                  </p>
                  {scoreSummary.points_behind_first > 0 && scoreSummary.rank > 1 && (
                    <p className="text-[#FF9100] text-xs font-bold mt-1">
                      {scoreSummary.points_behind_first} points behind #1
                    </p>
                  )}
                  {scoreSummary.rank === 1 && (
                    <p className="text-[#FFD700] text-xs font-bold mt-1">You're #1!</p>
                  )}
                </motion.div>

                {/* 3. SCORE BREAKDOWN */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="space-y-2"
                  data-testid="score-breakdown"
                >
                  {[
                    { label: 'Completion', value: scoreSummary.breakdown?.completion || 0, max: 40, color: '#10B981', icon: '✅' },
                    { label: 'Effort', value: scoreSummary.breakdown?.effort || 0, max: 10, color: '#2563EB', icon: '💪' },
                    { label: 'Streak', value: scoreSummary.breakdown?.streak || 0, max: 30, color: '#FF9100', icon: '🔥' },
                    { label: 'Competitive', value: scoreSummary.breakdown?.competitive || 0, max: 20, color: '#00E5FF', icon: '🎯' },
                  ].map(({ label, value, max, color, icon }) => (
                    <div key={label} className="flex items-center gap-3">
                      <span className="text-sm w-5 text-center">{icon}</span>
                      <span className="text-[11px] text-[#94A3B8] w-20">{label}</span>
                      <div className="flex-1 h-2 bg-[#060913] rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(value / max) * 100}%` }}
                          transition={{ delay: 0.8, duration: 0.5 }}
                          className="h-full rounded-full"
                          style={{ backgroundColor: color }}
                        />
                      </div>
                      <span className="text-[11px] text-white font-bold w-12 text-right">
                        {value}/{max}
                      </span>
                    </div>
                  ))}
                  {scoreSummary.points_left > 0 && (
                    <p className="text-[10px] text-[#94A3B8] text-center mt-1">
                      You left <span className="text-white font-bold">{scoreSummary.points_left} points</span> on the table
                    </p>
                  )}
                </motion.div>

                {/* 4. STREAK MOMENT */}
                {scoreSummary.current_streak > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="bg-[#FF9100]/10 border border-[#FF9100]/30 rounded-xl p-3 text-center"
                    data-testid="streak-section"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Flame size={20} className="text-[#FF9100]" />
                      <span className="font-['Barlow_Condensed'] text-2xl font-black text-[#FF9100]">
                        {scoreSummary.current_streak}-day streak
                      </span>
                    </div>
                    {scoreSummary.streak_next_level && (
                      <p className="text-[11px] text-[#FF9100] mt-1">{scoreSummary.streak_next_level}</p>
                    )}
                  </motion.div>
                )}

                {/* 5. SOCIAL PRESSURE */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="text-center"
                  data-testid="social-section"
                >
                  {scoreSummary.has_friends ? (
                    <p className="text-[#CBD5E1] text-xs">
                      <span className="text-white font-bold">{scoreSummary.friends_active_today}</span> of your friends trained today
                    </p>
                  ) : (
                    <p className="text-[#CBD5E1] text-xs">Invite your friends to develop and compete with you!</p>
                  )}
                </motion.div>

                {/* Badges */}
                {scoreSummary.badges?.length > 0 && (
                  <div className="flex justify-center gap-3 py-1" data-testid="badges-display">
                    {scoreSummary.badges.map((badge, i) => (
                      <motion.div
                        key={badge.name}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 300, delay: 0.9 + i * 0.1 }}
                        className="flex flex-col items-center gap-1"
                      >
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center border-2"
                          style={{ borderColor: badge.color, background: `${badge.color}15` }}
                        >
                          <Award size={20} style={{ color: badge.color }} />
                        </div>
                        <span className="text-[9px] font-bold" style={{ color: badge.color }}>
                          {badge.name}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* 6. CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0 }}
                >
                  <button
                    onClick={onContinue}
                    className="w-full bg-gradient-to-r from-[#10B981] to-[#059669] text-white font-bold uppercase tracking-wide rounded-lg px-8 py-4 transition-all flex items-center justify-center gap-2"
                    data-testid="day-done-continue-btn"
                  >
                    {scoreSummary.current_streak > 0 ? 'Keep Your Streak Alive Tomorrow' : 'Finish Strong Tomorrow'}
                    <ChevronRight size={18} />
                  </button>
                </motion.div>

                {/* 7. MICRO COPY */}
                <p className="text-[10px] text-[#64748B] text-center italic">{scoreSummary.micro_copy}</p>
              </>
            ) : (
              <div className="text-center py-4">
                <Trophy size={32} className="text-[#10B981] mx-auto mb-3" />
                <h2 className="font-['Barlow_Condensed'] text-2xl font-black">
                  {dayLabel.toUpperCase()} <span className="text-[#10B981]">COMPLETE</span>
                </h2>
                <button
                  onClick={onContinue}
                  className="mt-4 w-full bg-[#10B981] text-white font-bold uppercase rounded-lg px-8 py-4"
                  data-testid="day-done-continue-btn"
                >
                  Continue <ChevronRight size={18} className="inline" />
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
